import type { Media } from '@/components/marketing/Media';

/* ==========================================================================
   Every product screen the marketing site shows, in one place. The files are
   produced by scripts/capture-marketing.ts against the seeded Northbird
   Tavern workspace (scripts/marketing-seed.ts), run on an isolated stack with
   `scripts/marketing-stack.sh up` then `capture`, so re-running it refreshes
   every page at once. A page refers to a screen by name and never by path,
   which is what lets a still be swapped for a video without touching the
   page. Sizes are the captured files' pixel sizes (2x device scale).
   ========================================================================== */

const dir = '/marketing';

export const media = {
  displayNowPlaying: {
    kind: 'image',
    src: `${dir}/live-display-nowplaying.png`,
    alt: "The room display on a TV mid-set: the act on stage, the room code and QR code, who's up next, and the chat.",
    width: 3840,
    height: 2160,
  },
  phoneSettlement: {
    kind: 'image',
    src: `${dir}/phone-settlement.png`,
    alt: "A settlement on the act's phone: the door, the guarantee, 70% of the door, the split winning, and Sign off.",
    width: 824,
    height: 1740,
  },
  artistPhoneSettlement: {
    kind: 'image',
    src: `${dir}/artist-phone-settlement.png`,
    alt: "A settlement on the opener's phone: the door, the flat fee owed, and Sign off.",
    width: 824,
    height: 1740,
  },
  artistEventList: {
    kind: 'image',
    src: `${dir}/artist-event-list.png`,
    alt: "An artist's events page in Showliner: the band's own upcoming shows and a month calendar.",
    width: 2880,
    height: 1800,
  },
  artistEventDetail: {
    kind: 'image',
    src: `${dir}/artist-event-detail.png`,
    alt: 'A show the band booked itself: their own act confirmed, an opener invited, a venue that is not on Showliner, and the opener still owing a party size.',
    width: 2880,
    height: 1800,
  },
  artistPhoneInvite: {
    kind: 'image',
    src: `${dir}/artist-phone-invite.png`,
    alt: "The opener's invite on a phone with no account: the date, Accept and Decline, a question to answer, and the bill.",
    width: 824,
    height: 1740,
  },
  displayBeforeShow: {
    kind: 'image',
    src: `${dir}/live-display-signage.png`,
    alt: 'The room display before the first act: the bill in running order, the QR code, and the chat.',
    width: 3840,
    height: 2160,
  },
  adminEventList: {
    kind: 'image',
    src: `${dir}/admin-event-list.png`,
    alt: 'The events page in Showliner: upcoming shows with their booking status, and a month calendar.',
    width: 2880,
    height: 1800,
  },
  adminEventDetail: {
    kind: 'image',
    src: `${dir}/admin-event-detail.png`,
    alt: "A show page as the venue sees it: the bill with each act's booking status, and the schedule with the party sizes two acts still owe.",
    width: 2880,
    height: 1800,
  },
  adminStagePlot: {
    kind: 'image',
    src: `${dir}/admin-stage-plot.png`,
    alt: 'The stage plot editor: the house backline in orange on the stage, with the gear library beside it.',
    width: 2880,
    height: 1800,
  },
  phoneInvite: {
    kind: 'image',
    src: `${dir}/phone-invite.png`,
    alt: 'An invite opened on a phone with no account: the date, Accept and Decline, one advance question to answer, and the rest of the bill.',
    width: 824,
    height: 1740,
  },
  phoneRoom: {
    kind: 'image',
    src: `${dir}/live-audience-room.png`,
    alt: "A fan's phone in the room: the chat, and their balances of the venue's and the act's currency.",
    width: 824,
    height: 1740,
  },
  profile: {
    kind: 'image',
    src: `${dir}/public-performer-profile.png`,
    alt: "An act's public page on a phone: genres, bio, links to their music and merch, and upcoming shows.",
    width: 824,
    height: 1740,
  },
} satisfies Record<string, Media>;

/**
 * Show night as three real moments of one room (capture "room-sequence"),
 * each a fan's phone and the room's TV shot at the same time.
 */
export const roomFrames = [
  {
    tv: `${dir}/room-tv-0.png`,
    phone: `${dir}/room-phone-0.png`,
    caption: 'A fan has scanned the code and typed a message.',
  },
  {
    tv: `${dir}/room-tv-1.png`,
    phone: `${dir}/room-phone-1.png`,
    caption: 'They send it, and it shows up in the chat on the TV.',
  },
  {
    tv: `${dir}/room-tv-2.png`,
    phone: `${dir}/room-phone-2.png`,
    caption: 'They spend 75 Cheer on Fireworks, and they go off over the stage.',
  },
];
